import { useState } from "react";

import { useFeedback } from "../components/Feedback";
import { Card, ErrorBox, Loading, PageHeader } from "../components/Page";
import { api, TASKS_CHANGED } from "../lib/api";
import { day } from "../lib/format";
import { useApi } from "../lib/useApi";

interface Task {
  id: number; title: string; notes: string | null; assignee_id: number | null; assignee: string | null;
  created_by: string | null; created_at: string; done: boolean; done_at: string | null; done_by: string | null;
}
interface TaskList { people: { id: number; name: string }[]; open: number; tasks: Task[] }

/** The shared task list. Anyone signed in adds a task, gives it to someone and
 *  ticks it off; who added and who finished each one is kept. */
export function Tasks() {
  const { data, error, reload, setData } = useApi<TaskList>("/tasks");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({ title: "", notes: "", assignee_id: "" });
  const [showDone, setShowDone] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const { confirm } = useFeedback();

  async function run(p: Promise<TaskList>) {
    setBusy(true); setErr(null);
    try {
      setData(await p);
      window.dispatchEvent(new Event(TASKS_CHANGED));
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const open = data.tasks.filter((t) => !t.done);
  const done = data.tasks.filter((t) => t.done);
  const patch = (t: Task, body: Partial<{ title: string; notes: string | null; assignee_id: number | null; done: boolean }>) =>
    run(api.patch<TaskList>(`/tasks/${t.id}`, body));

  const row = (t: Task) => (
    <li key={t.id} className={`task${t.done ? " done" : ""}`} data-testid="task">
      <label className="check task-check">
        <input type="checkbox" checked={t.done} disabled={busy} aria-label={`${t.done ? "Reopen" : "Done"}: ${t.title}`}
               onChange={(e) => void patch(t, { done: e.target.checked })} />
      </label>
      <div className="task-body">
        {editing === t.id ? (
          <form className="form-grid one" onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            void patch(t, { title: String(f.get("title")), notes: String(f.get("notes")) || null }).then(() => setEditing(null));
          }}>
            <label className="field"><span className="field-label">Task</span><input name="title" required defaultValue={t.title} /></label>
            <label className="field"><span className="field-label">Notes</span><textarea name="notes" rows={3} defaultValue={t.notes ?? ""} /></label>
            <div className="form-actions">
              <button className="btn btn-primary btn-sm" type="submit" disabled={busy}>Save</button>
              <button className="btn btn-sm" type="button" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </form>
        ) : (
          <>
            <div className="task-title">{t.title}</div>
            {t.notes ? <div className="task-notes">{t.notes}</div> : null}
            <div className="muted small">
              {t.done ? <>Done{t.done_by ? ` by ${t.done_by}` : ""}, {day(t.done_at)}</>
                : <>Added{t.created_by ? ` by ${t.created_by}` : ""}, {day(t.created_at)}</>}
            </div>
          </>
        )}
      </div>
      <div className="task-side">
        {!t.done && (
          <select aria-label={`Who: ${t.title}`} value={t.assignee_id ?? ""} disabled={busy}
                  onChange={(e) => void patch(t, { assignee_id: e.target.value ? Number(e.target.value) : null })}>
            <option value="">Anyone</option>
            {data.people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        {t.done && t.assignee ? <span className="muted small">{t.assignee}</span> : null}
        {editing !== t.id && !t.done && (
          <button className="link-btn" onClick={() => setEditing(t.id)} aria-label={`Edit: ${t.title}`}>Edit</button>
        )}
        <button className="link-btn btn-ghost-danger" disabled={busy} aria-label={`Delete: ${t.title}`}
                onClick={async () => {
                  if (await confirm({ title: `Delete "${t.title}"?`, body: "For everyone; it cannot be undone.", confirm: "Delete", danger: true }))
                    void run(api.del(`/tasks/${t.id}`).then(() => api.get<TaskList>("/tasks")));
                }}>
          Delete
        </button>
      </div>
    </li>
  );

  return (
    <>
      <PageHeader title="Tasks" sub="One shared list for everyone signed in: add a task, give it to someone, tick it off." />
      <Card title="Add a task">
        <form className="form-grid" onSubmit={(e) => {
          e.preventDefault();
          void run(api.post<TaskList>("/tasks", { title: form.title, notes: form.notes || null,
            assignee_id: form.assignee_id ? Number(form.assignee_id) : null })).then(() => setForm({ title: "", notes: "", assignee_id: "" }));
        }}>
          <label className="field"><span className="field-label">Task</span>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Send the OPICI Diveport login" /></label>
          <label className="field"><span className="field-label">Who</span>
            <select value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}>
              <option value="">Anyone</option>
              {data.people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></label>
          <label className="field"><span className="field-label">Notes (optional)</span>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
          <div className="form-actions"><button className="btn btn-primary" type="submit" disabled={busy || !form.title.trim()}>Add</button></div>
        </form>
        {err ? <p className="down" role="alert">{err}</p> : null}
      </Card>

      <Card title={`To do (${open.length})`}>
        {open.length ? <ul className="tasks" data-testid="tasks-open">{open.map(row)}</ul>
          : <p className="note muted">Nothing to do. Add a task above.</p>}
      </Card>

      {done.length > 0 && (
        <Card title={`Done (${done.length})`} actions={
          <button className="link-btn" onClick={() => setShowDone((s) => !s)} aria-expanded={showDone}>{showDone ? "Hide" : "Show"}</button>}>
          {showDone ? <ul className="tasks" data-testid="tasks-done">{done.map(row)}</ul> : null}
        </Card>
      )}
    </>
  );
}
